import React, { useEffect, useRef, useState } from "react";
import {
  FiSearch,
  FiFilter,
  FiMoreVertical,
  FiUser,
  FiMail,
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
  FiBookOpen,
  FiAward,
  FiClock,
  FiEdit2,
  FiToggleLeft,
  FiToggleRight,
  FiUsers,
  FiTrendingUp,
  FiTrendingDown,
  FiArrowUp,
  FiArrowDown,
  FiRefreshCw,
  FiEye,
  FiTrash2,
  FiHash,
  FiTag,
  FiInfo,
  FiAlertCircle,
} from "react-icons/fi";
import {
  MdOutlineSearch,
  MdOutlineFilter,
  MdOutlinePerson,
  MdOutlineEmail,
  MdOutlineCalendarToday,
  MdOutlineCheckCircle,
  MdOutlineCancel,
  MdOutlinePending,
  MdOutlineBook,
  MdOutlineAward,
  MdOutlineAccessTime,
  MdOutlineEdit,
  MdOutlineDelete,
  MdOutlineVisibility,
  MdOutlineRefresh,
  MdOutlineInfo,
} from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import moment from "moment";
import { BsThreeDotsVertical } from "react-icons/bs";
import ReactPaginate from "react-paginate";
import SortableHeader from "../../../components/SortableHeader";

const LearningManagementTable = ({
  TableName,
  TableHeader,
  TableData,
  handleEdit,
  handleViewLearner,
  handleUserBlock,
  handleFilter,
  showMenu,
  total,
  size,
  isMenu,
  clickedUserId,
  handlePageClick,
  offset,
  keyword,
  setkeyword,
  currentSortKey,
  currentSortOrder,
  onSort,
}) => {
  const contentActionRef = useRef(null);
  const [imgError, setImgError] = useState(false);

  // Normalize headers: supports both string[] and {label, sortKey}[]
  const normalizedHeaders =
    TableHeader?.map((h) =>
      typeof h === "string" ? { label: h, sortKey: null } : h
    ) || [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contentActionRef.current &&
        !contentActionRef.current.contains(event.target)
      ) {
        showMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  // Status badge component
  const StatusBadge = ({ isDisable }) => {
    if (!isDisable) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full">
          <FiCheckCircle className="w-3.5 h-3.5 text-emerald-500" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full">
        <FiXCircle className="w-3.5 h-3.5 text-rose-500" />
        Disabled
      </span>
    );
  };

  // Course stats badge
  const CourseStats = ({ count, label }) => (
    <div className="flex items-center gap-1">
      <FiBookOpen className="w-3.5 h-3.5 text-[#715EFE]" />
      <span className="text-sm font-medium text-gray-700">{count || 0}</span>
    </div>
  );

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100/80 overflow-hidden">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
              <FiUsers className="w-4 h-4 text-[#715EFE]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">
                {TableName}
              </h2>
              <p className="text-xs text-gray-400 hidden sm:block">
                {TableData?.length || 0} learners
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {/* Search */}
            <div className="relative flex-1 sm:min-w-[180px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search learners..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                onChange={(e) => setkeyword(e.target.value)}
                value={keyword}
              />
            </div>

            {/* Filter */}
            <div className="relative min-w-[120px]">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiFilter className="text-gray-400 w-4 h-4" />
              </div>
              <select
                className="appearance-none w-full h-10 pl-9 pr-8 py-2 bg-white border border-gray-200 text-sm text-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                onChange={handleFilter}
              >
                <option value="">All Status</option>
                <option value="enable">Active</option>
                <option value="disable">Disabled</option>
              </select>
              <FiClock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                {normalizedHeaders.map((heading, index) => {
                  const headerIcons = {
                    "#": null,
                    "Learner Name": FiUser,
                    Email: FiMail,
                    "Course Enrolled": FiBookOpen,
                    "Course Completed": FiAward,
                    Status: FiCheckCircle,
                    "Join Date": FiCalendar,
                    Action: null,
                  };
                  const Icon = headerIcons[heading.label] ?? null;
                  return (
                    <th
                      key={index}
                      scope="col"
                      className="px-3 md:px-4 py-3 whitespace-nowrap"
                    >
                      <SortableHeader
                        label={heading.label}
                        sortKey={heading.sortKey}
                        currentSortKey={currentSortKey}
                        currentSortOrder={currentSortOrder}
                        onSort={onSort}
                        icon={Icon}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {TableData && TableData.length > 0 ? (
                TableData?.map((learner, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50/70 transition-colors duration-150 group"
                  >
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm font-medium text-gray-400">
                        {offset + index + 1}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-3">
                        {!imgError && learner?.profileImage ? (
                          <img
                            src={learner.profileImage}
                            alt="Avatar"
                            className="w-9 h-9 rounded-full object-cover border-2 border-gray-200"
                            onError={() => setImgError(true)}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center border-2 border-gray-200">
                            <FiUser className="text-[#715EFE] text-sm" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p
                            className="text-sm font-semibold text-gray-800 truncate max-w-[120px] md:max-w-[180px] capitalize"
                            title={learner?.fullName}
                          >
                            {learner?.fullName?.replace(/_/g, " ") || "Unknown"}
                          </p>
                          {learner?.isDeleted && (
                            <span className="text-[10px] font-medium text-rose-500">
                              Account Deleted
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm text-gray-600 truncate max-w-[150px] block">
                        {learner?.email || "N/A"}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <CourseStats count={learner?.courseBuyed} />
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <CourseStats count={learner?.courseCompleted} />
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <StatusBadge isDisable={learner?.isDisable} />
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700 whitespace-nowrap">
                          {learner?.createdAt
                            ? moment(learner.createdAt).format("MMM D, YYYY")
                            : "N/A"}
                        </span>
                        {learner?.createdAt && (
                          <span className="text-xs text-gray-400">
                            {moment(learner.createdAt).format("h:mm A")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="relative">
                        <button
                          className="p-1.5 text-gray-400 hover:text-[#715EFE] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                          onClick={() => showMenu(learner?._id)}
                          aria-label="Options"
                        >
                          <BsThreeDotsVertical className="w-4 h-4" />
                        </button>
                        {isMenu && clickedUserId === learner?._id && (
                          <div
                            className="absolute right-0 z-50 w-40 mt-1 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                            ref={contentActionRef}
                          >
                            <div className="py-1">
                              <button
                                className="flex items-center gap-3 px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-[#725EFE33] hover:text-[#715EFE] transition-colors duration-150"
                                onClick={() => handleEdit(learner)}
                              >
                                <FiEdit2 className="w-4 h-4" />
                                <span>Edit</span>
                              </button>
                              <button
                                className="flex items-center gap-3 px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-[#725EFE33] hover:text-[#715EFE] transition-colors duration-150"
                                onClick={() => handleUserBlock(learner)}
                              >
                                {learner?.isDisable ? (
                                  <>
                                    <FiCheckCircle className="w-4 h-4 text-emerald-500" />
                                    <span>Activate</span>
                                  </>
                                ) : (
                                  <>
                                    <FiXCircle className="w-4 h-4 text-rose-500" />
                                    <span>Disable</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={normalizedHeaders.length || 8}
                    className="px-4 py-12 text-center"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-4 bg-gray-50 rounded-full">
                        <FiUsers className="w-10 h-10 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">
                        No learners found
                      </p>
                      <p className="text-xs text-gray-400">
                        Try adjusting your search or filters
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {TableData && TableData.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <div className="text-sm text-gray-500">
              Showing {offset + 1} to {Math.min(offset + size, total)} of {total}{" "}
              entries
            </div>
            <ReactPaginate
              previousLabel={"Prev"}
              nextLabel={"Next"}
              breakLabel={"..."}
              breakClassName={"px-2 text-gray-400"}
              pageCount={Math.ceil(total / size)}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"flex items-center gap-1"}
              pageClassName={"px-2"}
              pageLinkClassName={
                "px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200"
              }
              previousClassName={"px-2"}
              previousLinkClassName={
                "px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200"
              }
              nextClassName={"px-2"}
              nextLinkClassName={
                "px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200"
              }
              activeClassName={"bg-[#715EFE] text-white rounded-md"}
              activeLinkClassName={
                "bg-[#715EFE] text-white px-3 py-1 rounded-md"
              }
              disabledClassName={"opacity-50 cursor-not-allowed"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningManagementTable;